import { Button, Checkbox, LegacyFilters, Text } from "@shopify/polaris"
import classNames from "classnames"
import { debounce } from "lodash-es"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import DraggableList from "@/component/DraggableList"
import LazyLoadImage from "@/component/LazyLoadImage"
import { CourierType } from "@/pages/Settings/components/CourierMatching/index"

import styles from "./CourierDraggableTable.module.scss"


interface CourierDraggableTableProps{
  queryValue: string;
  emptyState?: React.ReactNode;
  items: CourierType[];
  selections: string[];
  onSelectionChange(items: string[]): void;
  onQueryChange(queryValue: string): void;
  onSortChange(list: CourierType[]): void;
  onDisabled?(): void;
}

const expressLogoUrl = "//cdn.parcelpanel.com/assets/common/images/express/"

export default function CourierDraggableTable({
  items,
  queryValue,
  selections,
  emptyState,
  onQueryChange,
  onSelectionChange,
  onSortChange,
  onDisabled,
}:CourierDraggableTableProps){
  const { t } = useTranslation(["settings", "common"])
  const [queryResult, setQueryResult] = useState<CourierType[]>([])

  // 当items变化时，如果有查询条件，需要重新过滤queryResult
  useEffect(() => {
    if (queryValue) {
      setQueryResult(selectMatchItem<CourierType>(items, queryValue))
    }
  }, [items, queryValue])

  const handleQueryClear = () => {
    onQueryChange("")
    setQueryResult([])
  }

  const debounceQuery = useMemo(() => debounce((items, queryValue) => {
    // 清空所有选中项
    onSelectionChange([])
    setQueryResult(selectMatchItem<CourierType>(items, queryValue))
  }, 200), [onSelectionChange])

  const handleQueryChange = (queryValue: string) => {
    onQueryChange(queryValue)
    debounceQuery(items, queryValue)
  }

  const filterItems = queryValue ? queryResult : items

  const checkboxChecked = selections.length > 0 && selections.length === filterItems.length ? true
    : selections.length > 0 ? "indeterminate" : false


  return <div>
    <div className={"p-4 pb-1"}>
      <LegacyFilters
        queryPlaceholder={t("settings:CourierMatching.SearchCouriers")}
        disabled={!items.length}
        filters={[]}
        queryValue={queryValue}
        onClearAll={handleQueryClear}
        onQueryChange={handleQueryChange}
        onQueryClear={handleQueryClear}
      />
    </div>

    {
      !!filterItems.length && <div className={styles.checkboxBar}>
        <Checkbox
          label={<Text as={"span"} variant={"bodySm"} fontWeight={"medium"}>
            {checkboxChecked
              ? <div className={"flex gap-4 items-center"}>
                <span>{t("settings:CourierMatching.selectedCount", { count: selections.length })}</span>
                <Button onClick={onDisabled}>{selections.length > 1 ? t("settings:RemoveCarriers") : t("settings:RemoveCarrier")}</Button>
              </div>
              : <div className={"mb-[1px]"}>{t("settings:CourierMatching.ShowingCouriers", { count: filterItems.length })}</div>
            }
          </Text>}
          checked={checkboxChecked}
          onChange={(checked) => {
            if (checked) {
              onSelectionChange(filterItems.map(item => item.express))
            } else {
              onSelectionChange([])
            }
          }}
        />
      </div>
    }

    {
      filterItems.length === 0
        ? emptyState
        : <DraggableList
          className={styles.resourceList}
          cardClassName={classNames(styles.cardWrapper)}
          disabled={queryValue !== ""}
          cards={filterItems
            .map(item => {
              return {
                id: item.express,
                ...item,
                checked: selections.includes(item.express),
                children: <div>
                  <Checkbox
                    label={<div className={"flex items-center gap-3"}>
                      <LazyLoadImage className={"rounded overflow-hidden"} key={item.express} style={{ width: "44px", height: "44px" }} src={`${expressLogoUrl}${item.img}`} alt={`${item.name} courier LOGO`} />
                      <Text as={"p"}>{item.name}</Text>
                    </div>}
                    checked={selections.includes(item.express)}
                    onChange={(e) => {
                      if (e && !selections.includes(item.express)) {
                        onSelectionChange([...selections, item.express])
                      } else if (!e && selections.includes(item.express)){
                        onSelectionChange(selections.filter(selection => selection !== item.express))
                      }
                    }}
                  />
                </div>,
              }
            })}
          setCards={(e) => {
            e.map(item => {
              delete item.id
              delete item.children
            })
            onSortChange(e)
          }}
        />
    }


  </div>
}


/** 模糊搜索对象 */
function selectMatchItem<IItemType>(items: IItemType[], keyword: string) {
  try {
    const escapedQuery = keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    const reg = new RegExp(escapedQuery, "gi")

    // @ts-ignore
    return items.filter(item => Object.values(item)
      .some(v => reg.test(v as string)))
  } catch (e){
    console.log(e)
  }
}
